import { useState } from 'react'
import Link from 'next/link'
import { withFormik, Form, Field, FormikProps } from 'formik'
import * as Yup from 'yup'
import { useSupabaseClient } from '@supabase/auth-helpers-react'

import classNames from 'classnames'

import Popup from '@/components/Popup'
import Button from '@/components/base/Button'

interface SignInEmailValues {
  email: string,
}

interface SignInOTPValues {
  otp: string,
}

type OtherProps = {
  userAction: any,
  serverErrors?: any,
}

const EmailBaseForm = (props: OtherProps & FormikProps<SignInEmailValues>) => {
  const { touched, errors, isSubmitting, serverErrors } = props

  const fieldClasses = 'form-input text-base text-slate w-full mb-2.5 pt-2 pb-1.5 px-3 mx-0 rounded-[20px] border border-slate bg-white placeholder:text-[rgba(80,80,80,0.5)]'
  const errorClasses = 'text-xs uppercase mb-1 text-slate tracking-wider mb-1.5 ml-3'

  return (
    <Form
      className={
        classNames({
          'flex flex-col': true,
          'opacity-40': isSubmitting,
        })
      }
    >
      <div className="w-full">
        {serverErrors && serverErrors.length > 0 && (
          <div className="text-sm text-grey !mt-2.5 mb-1.5 pl-3">There was an issue with your sign in: <span className={errorClasses + ' !ml-1'}>{serverErrors.join(', ')}</span></div>
        )}
      </div>
      <div className="w-full">
        {touched.email && errors.email && (<p className={errorClasses}>{errors.email}</p>)}
        <Field name="email" type="email" placeholder="Email" className={fieldClasses} />
      </div>
      <div className="w-full">
        <Button
          text="Sign In"
          size="regular"
          classes="text-center !w-full h-[34px] px-12 min-w-[160px] max-w-[unset] border-slate hover:!bg-white"
          color="dark"
          disabled={isSubmitting}
        />
        {isSubmitting ? <p className="text-sm uppercase mb-1 text-white">Loading...</p> : <p className="text-base mb-1 text-white"><Link href="/account/reset-password" className="text-white">Forgetten password?</Link></p>}
      </div>
    </Form>
  )
}

const OTPBaseForm = (props: OtherProps & FormikProps<SignInOTPValues>) => {
  const { touched, errors, isSubmitting, serverErrors } = props

  const fieldClasses = 'form-input text-base tracking-[3px] text-slate w-full mb-2.5 pt-2 pb-1.5 px-3 mx-0 rounded-[20px] border border-slate bg-white placeholder:text-[rgba(80,80,80,0.5)]'
  const errorClasses = 'text-xs uppercase mb-1 text-slate tracking-wider mb-1.5 ml-3'

  return (
    <Form
      className={
        classNames({
          'flex flex-col': true,
          'opacity-40': isSubmitting,
        })
      }
    >
      <div className="w-full">
        {serverErrors && serverErrors.length > 0 && (
          <div className="text-sm text-grey !mt-2.5 mb-1.5 pl-3">There was an issue with your sign in: <span className={errorClasses + ' !ml-1'}>{serverErrors.join(', ')}</span></div>
        )}
      </div>
      <div className="w-full">
        {touched.otp && errors.otp && (<p className={errorClasses}>{errors.otp}</p>)}
        <Field type="text" name="otp" placeholder="______" className={fieldClasses} />
      </div>
      <div className="w-full">
        <Button
          text="Verify"
          size="small"
          classes="text-center"
          color="dark"
          disabled={isSubmitting}
        />
        {isSubmitting ? <p className="text-sm uppercase mb-1 text-white">Loading...</p> : <p className="text-base mb-1 text-white"><Link href="/account/reset-password" className="text-white">Forgetten password?</Link></p>}
      </div>
    </Form>
  )
}

interface FormProps {
  errors?: string,
  handleSubmit: any,
  serverErrors?: any,
}

const SignInEmailForm = withFormik<FormProps, SignInEmailValues>({
  mapPropsToValues: () => ({
    email: '',
  }),
  validationSchema: Yup.object({
    email: Yup.string().email('Invalid email address').required('Email is Required'),
  }),
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    setSubmitting(true)

    props.handleSubmit(values)
  }
})(EmailBaseForm)

const SignInOTPForm = withFormik<FormProps, SignInOTPValues>({
  mapPropsToValues: () => ({
    otp: '',
  }),
  validationSchema: Yup.object({
    otp: Yup.string().required('Verification code is Required'),
  }),
  handleSubmit: async (values, { props, setSubmitting, setErrors }) => {
    setSubmitting(true)

    console.log('otp form values', values)

    props.handleSubmit(values)
  }
})(OTPBaseForm)

export default function UnAuth(props: any) {
  const [serverErrors, setServerErrors] = useState<any>([])
  const [step, setStep] = useState<number>(1)
  const [email, setEmail] = useState<string>('')

  const supabase = useSupabaseClient()

  const handleEmailSignIn = async (values: { email: string }) => {
    console.log('email sign in', values)
    setEmail(values.email)

    const { data, error } = await supabase.auth.signInWithOtp({
      email: values.email,
    })

    console.log('data', data)

    if (data) {
      setStep(2)
    }

    if (error) {
      setServerErrors([error.message])
      return 
    }
  }
  const handleOTPVerify = async (values: { otp: string }) => {
    console.log('otp', values)
    const { data, error } = await supabase.auth.verifyOtp({
      email,
      token: values?.otp,
      type: 'email',
    })

    if (error) {
      setServerErrors([error.message])
      return 
    }

    if (data) {
      window.location.href = ''
    }
  }

  return (
    <section
      className={
        classNames({
          'flex flex-col items-center justify-center w-full h-full mobile:px-2.5': true,
        })
      }
    >
      <Popup
        active={true}
        close={() => {}}
        hideClose={true}
        style="white"
        width="medium"
      >
        <header className="mb-12 mobile:mb-6">
          <h2 className="text-base uppercase mb-1 mobile:text-m-base">Sign In to Continue</h2>
          <p className="text-md mobile:text-m-md">
            All of our practices are only available for signed in members. If you do not have an account you can sign up <Link href="/account/create" className="underline">here</Link>.
          </p>
        </header>
        {step === 1 && (
          <SignInEmailForm
            handleSubmit={handleEmailSignIn}
            serverErrors={serverErrors}
          />
        )}
        {step === 2 && (
          <SignInOTPForm
            handleSubmit={handleOTPVerify}
            serverErrors={serverErrors}
          />
        )}
      </Popup>
    </section>
  )
}
